import { createClient, type RequestOptions } from '@smbsuite/api';
import { reportReachable } from '../app/status-page';
import { accessToken, userManager } from '../auth/oidc';

export { ApiError, isApiError, type RequestOptions } from '@smbsuite/api';

// The web app's way of calling the platform (TF-269: the call itself is @smbsuite/api's, shared with
// the phone app): on this origin, with the signed-in person's token; without one, signing in starts
// again and comes back to this page.
const client = createClient({
  token: accessToken,
  signedOut: () => userManager.signinRedirect({ state: { returnTo: location.pathname + location.search } }),
  reachable: reportReachable,
});

/** Calls a platform API on this origin. Resolves to the parsed JSON body (undefined for 204). */
export const api = client.call;

/**
 * A file the browser should keep rather than show: the same call as api(), but the answer is bytes
 * and the name comes from the server. Saving it needs a link the browser can click, so the caller
 * gets an object URL it must revoke when it is done with it.
 */
export async function apiDownload(path: string, options: RequestOptions = {}): Promise<{ url: string; fileName: string }> {
  const response = await client.get(path, options);
  const disposition = response.headers.get('content-disposition') ?? '';
  const named = /filename="([^"]+)"/.exec(disposition);
  return { url: URL.createObjectURL(await response.blob()), fileName: named?.[1] ?? 'download' };
}

/**
 * An image the API keeps behind sign-in (a contact's photo, TF-310), as a data: URL an <img> can
 * show. Nothing to revoke: it goes when the cache lets it go. The browser's own cache answers a
 * repeat when the server said the address never changes.
 */
export async function apiDataUrl(path: string, options: RequestOptions = {}): Promise<string> {
  const blob = await (await client.get(path, options)).blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the image'));
    reader.readAsDataURL(blob);
  });
}

/** Saves what apiDownload fetched, under the name the server gave it. */
export function saveDownload({ url, fileName }: { url: string; fileName: string }): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
  // Revoked once the browser has taken it; immediately would cancel the save in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
